import type { GemxQlQueryVariables } from '~/core/gemxql/graphql';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { ETimeDimension } from '~/core/gemxql/types';

import {
  EAnalyticSource,
  EComparisonOperator,
  EFilterField,
  EGroupWithClause,
  EOrderDirectionType,
} from '~/core/gemxql/types';
import {
  CAMPAIGN_BREAKDOWN_FIELDS,
  CAMPAIGN_LISTING_METRICS,
  EXPERIMENT_FIELD,
} from '../constants/campaign-metrics-v2';
import { buildGemXQlQuery } from '~/core/gemxql/builder';

export interface IBuildCampaignGroupQueryVariablesParams {
  mode: EAnalyticMode;
  campaignGroupID: string;
  /** Version id of the picked date range, so the query is scoped to it and doesn't merge in other versions' traffic. Omitted for preset ranges. */
  campaignGroupVersionID?: string;
  dateRange: AnalyticDateFilter;
  /** When set, query is time-series mode. When omitted, listing-only (one row per variant). */
  interval?: ETimeDimension;
}

export const buildCampaignGroupQueryVariables = ({
  mode,
  campaignGroupID,
  campaignGroupVersionID,
  dateRange,
  interval,
}: IBuildCampaignGroupQueryVariablesParams): GemxQlQueryVariables => {
  // Group by the A/B side, not by variant id: a multipage group has one variant per page, so
  // per-variant rows would have to be summed client-side and a visitor crossing two pages would
  // be counted twice. `version` lets the server aggregate each side distinctly, like the legacy
  // `useTemplateGroup` query did. Side FIRST so per-partition gap fill keeps each series
  // continuous (per docs §TIMESERIES) and GROUP_TOTALS subtotals by that same dimension.
  const dimensions = interval ? [EXPERIMENT_FIELD.VERSION, interval] : [EXPERIMENT_FIELD.VERSION];

  const input = buildGemXQlQuery({
    mode,
    sources: [EAnalyticSource.SESSIONS],
    metrics: [...CAMPAIGN_LISTING_METRICS, ...CAMPAIGN_BREAKDOWN_FIELDS],
    dimensions,
    timeDimension: interval,
    // Subtotals per side are only needed once the side is split across time buckets; with a single
    // dimension the server emits no `___<dimension>___totals` column and each row is the total.
    groupWithClause: interval ? EGroupWithClause.ALL : EGroupWithClause.NONE,
    dateRange,
    filters: {},
    versionIds: { [EFilterField.GROUP_CAMPAIGN_VERSION_ID]: campaignGroupVersionID },
    extraConditions: [
      {
        operator: EComparisonOperator.EQ,
        fields: [EXPERIMENT_FIELD.EXPERIMENT_GROUP_ID],
        value: campaignGroupID,
      },
    ],
    orderBy: interval
      ? {
          [EXPERIMENT_FIELD.VERSION]: {
            field: EXPERIMENT_FIELD.VERSION,
            direction: EOrderDirectionType.ASC,
          },
          [interval]: { field: interval, direction: EOrderDirectionType.ASC },
        }
      : undefined,
  });

  return { input };
};
